import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AreaChart, Area, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
  Line, LineChart, CartesianGrid, Legend,
} from "recharts";
import {
  Plus, Globe2, ArrowUpRight, ArrowDownRight, Trash2, AlertCircle,
  TrendingUp, Eye, BarChart3, Calculator, Heart, Sparkles, Pencil,
  CircleDollarSign, MoreVertical, Plus as PlusIcon,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import UsReturnProjectionPanel from "./UsReturnProjectionPanel";
import UsPortfolioHealthTab from "./UsPortfolioHealthTab";

const fmtUsd = (v, opts = {}) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (opts.compact && Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (opts.compact && Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtPct = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(d)}%`);

const SIGNAL_CLASS = {
  STRONG_BUY: "bg-emerald-600 text-white",
  BUY: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30",
  WATCH: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30",
  HOLD: "bg-muted text-muted-foreground",
  AVOID: "bg-destructive/15 text-destructive border border-destructive/30",
};
const SignalBadge = ({ s }) => s ? (
  <Badge className={`${SIGNAL_CLASS[s] || ""} text-[10px] font-medium whitespace-nowrap`}>
    {s.replace("_", " ")}
  </Badge>
) : <span className="text-xs text-muted-foreground">—</span>;

const VAL_META = {
  "Deep Value":   { cls: "bg-[#14532d] text-white border-[#14532d]",  icon: "💎" },
  "Undervalued":  { cls: "bg-[#16a34a] text-white border-[#16a34a]",  icon: "📉" },
  "Fair Value":   { cls: "bg-[#6b7280] text-white border-[#6b7280]",  icon: "⚖" },
  "Overvalued":   { cls: "bg-[#ea580c] text-white border-[#ea580c]",  icon: "📈" },
  "Overpriced":   { cls: "bg-[#dc2626] text-white border-[#dc2626]",  icon: "🔴" },
};
const ValBadge = ({ s }) => {
  if (!s) return <span className="text-xs text-muted-foreground">—</span>;
  const meta = VAL_META[s];
  if (!meta) return <Badge variant="outline" className="text-[10px]">{s}</Badge>;
  return (
    <Badge variant="outline" className={`${meta.cls} text-[10px] font-medium whitespace-nowrap inline-flex items-center gap-1 px-2 py-0.5`}>
      <span aria-hidden="true" className="leading-none">{meta.icon}</span>
      {s}
    </Badge>
  );
};

// ---- Add Holding Dialog ---------------------------------------------------

const AddHoldingDialog = ({ open, onOpenChange, portfolioId, onAdded, seedSymbol, seedPrice }) => {
  const [symbol, setSymbol] = useState("");
  const [shares, setShares] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapError, setSnapError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // When opened from "Add more shares" on an existing holding, seed the
  // symbol + last price so the user can just type the additional share
  // count and submit.
  useEffect(() => {
    if (open && seedSymbol) {
      setSymbol(seedSymbol);
      if (seedPrice) setPurchasePrice(String(Number(seedPrice).toFixed(2)));
    }
  }, [open, seedSymbol, seedPrice]);

  // Debounced snapshot fetch
  useEffect(() => {
    const sym = (symbol || "").trim().toUpperCase();
    if (!open || !sym) { setSnapshot(null); setSnapError(null); return; }
    const t = setTimeout(async () => {
      setSnapLoading(true); setSnapError(null);
      try {
        const data = await apiGet(`/us-stocks/${encodeURIComponent(sym)}/snapshot`);
        setSnapshot(data);
        if (data && data.current_price && !purchasePrice) {
          setPurchasePrice(String(data.current_price.toFixed(2)));
        }
      } catch (e) {
        setSnapshot(null);
        setSnapError(e?.response?.data?.detail || `Unknown US symbol: ${sym}`);
      } finally {
        setSnapLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, open]);

  const reset = () => {
    setSymbol(""); setShares(""); setPurchasePrice(""); setSnapshot(null); setSnapError(null);
  };

  const onSubmit = async () => {
    if (!symbol || !shares || !purchasePrice) {
      toast.error("Symbol, shares and purchase price are required.");
      return;
    }
    setSubmitting(true);
    try {
      await apiPost(`/us-portfolios/${portfolioId}/holdings`, {
        us_symbol: symbol.toUpperCase().trim(),
        shares: parseFloat(shares),
        purchase_price: parseFloat(purchasePrice),
      });
      toast.success(`Added ${shares} ${symbol.toUpperCase()} @ $${purchasePrice}`);
      reset();
      onOpenChange(false);
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add holding");
    } finally {
      setSubmitting(false);
    }
  };

  const investedPreview = (() => {
    const s = parseFloat(shares); const p = parseFloat(purchasePrice);
    if (isNaN(s) || isNaN(p)) return null;
    return s * p;
  })();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="us-add-holding-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4" /> Add US Holding</DialogTitle>
          <DialogDescription>USD-only. Live signal & valuation shown so you can see what you're buying.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="us-symbol">Symbol</Label>
            <Input
              id="us-symbol"
              data-testid="us-add-holding-symbol"
              placeholder="e.g. AAPL, MSFT, JNJ…"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              autoComplete="off"
            />
          </div>

          {/* Snapshot preview card */}
          {symbol ? (
            <Card className="border-dashed">
              <CardContent className="pt-4 pb-4">
                {snapLoading ? (
                  <div className="space-y-2"><Skeleton className="h-4 w-1/2" /><Skeleton className="h-4 w-2/3" /></div>
                ) : snapError ? (
                  <div className="text-xs text-destructive flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5" /> {snapError}</div>
                ) : snapshot ? (
                  <div data-testid="us-add-holding-snapshot" className="space-y-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <div>
                        <div className="font-mono text-base font-semibold">{snapshot.symbol}</div>
                        <div className="text-xs text-muted-foreground">{snapshot.company_name} · {snapshot.sector}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">Current price</div>
                        <div className="text-lg font-semibold tabular-nums" data-testid="us-add-holding-current-price">
                          {fmtUsd(snapshot.current_price)}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <SignalBadge s={snapshot.signal} />
                      <ValBadge s={snapshot.valuation_status} />
                      <span className="text-xs text-muted-foreground">
                        Target {fmtUsd(snapshot.base_target_price)} ({snapshot.upside_pct == null ? "—" : (snapshot.upside_pct >= 0 ? "+" : "") + fmtPct(snapshot.upside_pct)})
                      </span>
                      <Link
                        to={`/us-stocks/${encodeURIComponent(snapshot.symbol)}`}
                        target="_blank" rel="noreferrer"
                        className="ml-auto text-xs text-primary hover:underline inline-flex items-center gap-0.5"
                      >
                        View analysis <Eye className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="us-shares">Shares</Label>
              <Input
                id="us-shares"
                data-testid="us-add-holding-shares"
                type="number" step="any" min="0"
                placeholder="50"
                value={shares}
                onChange={(e) => setShares(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="us-price">Purchase price (USD)</Label>
              <Input
                id="us-price"
                data-testid="us-add-holding-price"
                type="number" step="any" min="0"
                placeholder="260.00"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
              />
            </div>
          </div>

          {investedPreview != null ? (
            <div className="rounded-lg bg-muted/40 p-3 text-sm flex items-center justify-between">
              <span className="text-muted-foreground">Invested</span>
              <span className="font-semibold tabular-nums">{fmtUsd(investedPreview)}</span>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={onSubmit}
            disabled={submitting || !symbol || !shares || !purchasePrice || !snapshot}
            data-testid="us-add-holding-submit"
          >
            {submitting ? "Adding…" : "Add holding"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Create US Portfolio Dialog -------------------------------------------

const STYLE_META = {
  growth: {
    label: "Growth",
    icon: "📈",
    description: "Tech, healthcare, consumer disc — capital appreciation focus.",
  },
  balanced: {
    label: "Balanced",
    icon: "⚖",
    description: "Diversified mix across all major sectors. Default.",
  },
  income: {
    label: "Income",
    icon: "💰",
    description: "Financials, utilities, REITs — dividend yield focus.",
  },
};

const RISK_PROFILES = [
  { value: "conservative", label: "Conservative" },
  { value: "moderate", label: "Moderate" },
  { value: "aggressive", label: "Aggressive" },
];

const CreateUsPortfolioDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [style, setStyle] = useState("balanced");
  const [risk, setRisk] = useState("moderate");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const created = await apiPost("/us-portfolios", {
        portfolio_name: name || "My US Portfolio",
        investment_amount: amount ? parseFloat(amount) : null,
        investment_style: style,
        risk_profile: risk,
      });
      toast.success("US portfolio created", {
        description: `Style: ${STYLE_META[style].label} · Risk: ${risk}`,
      });
      onOpenChange(false);
      setName(""); setAmount(""); setStyle("balanced"); setRisk("moderate");
      onCreated?.(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="us-create-portfolio-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Globe2 className="h-4 w-4" /> Create US Portfolio</DialogTitle>
          <DialogDescription>
            USD-only portfolio. Style sets your default sector targets and AI-suggestion strategy.
            CSE portfolios remain entirely separate (no currency conversion).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="us-portfolio-name">Portfolio name</Label>
            <Input
              id="us-portfolio-name"
              data-testid="us-create-portfolio-name"
              placeholder="e.g. My US Tech Portfolio"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="us-portfolio-amount">Initial investment (USD, optional)</Label>
            <Input
              id="us-portfolio-amount"
              data-testid="us-create-portfolio-amount"
              type="number" min="0" step="any"
              placeholder="50000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          {/* Investment style — drives default sector targets */}
          <div className="space-y-2">
            <Label>Investment style</Label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(STYLE_META).map(([key, meta]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStyle(key)}
                  data-testid={`us-create-portfolio-style-${key}`}
                  className={`rounded-md border p-2.5 text-left transition-colors ${
                    style === key
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border hover:bg-muted"
                  }`}
                >
                  <div className="text-base">{meta.icon}</div>
                  <div className="text-sm font-medium mt-0.5">{meta.label}</div>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug">
              {STYLE_META[style].description}
            </p>
          </div>

          {/* Risk profile */}
          <div className="space-y-1.5">
            <Label htmlFor="us-portfolio-risk">Risk profile</Label>
            <Select value={risk} onValueChange={setRisk}>
              <SelectTrigger id="us-portfolio-risk" data-testid="us-create-portfolio-risk">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RISK_PROFILES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="us-create-portfolio-submit">
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Vs Benchmark chart ---------------------------------------------------

const VsBenchmarkChart = ({ portfolioId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("90");

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    apiGet(`/us-portfolios/${portfolioId}/vs-benchmark?days=${days}`)
      .then((d) => { if (!cancel) setData(d); })
      .catch(() => { if (!cancel) setData({ portfolio: [], benchmark: [] }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [portfolioId, days]);

  const merged = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    (data.portfolio || []).forEach((r) => map.set(r.date, { date: r.date, portfolio: r.value }));
    (data.benchmark || []).forEach((r) => {
      const e = map.get(r.date) || { date: r.date };
      e.benchmark = r.value;
      map.set(r.date, e);
    });
    return Array.from(map.values()).sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [data]);

  return (
    <Card>
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <BarChart3 className="h-4 w-4" /> Portfolio vs S&P 500 (^GSPC) · indexed to 100
        </CardTitle>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-28 h-8" data-testid="us-bench-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="180">180 days</SelectItem>
            <SelectItem value="252">1 year</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : merged.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
            Need at least 2 days of holdings price data to compute the comparison.
          </div>
        ) : (
          <div className="h-64" data-testid="us-bench-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={merged} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={36}
                  tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })} />
                <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]} tickFormatter={(v) => Number(v).toFixed(0)} />
                <RTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="portfolio" name="Your portfolio" stroke="hsl(var(--primary))" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="benchmark" name="S&P 500" stroke="hsl(var(--muted-foreground))" dot={false} strokeDasharray="4 4" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---- Edit Holding Dialog --------------------------------------------------
//
// Mirrors the CSE HoldingDialog "edit" mode but USD-only. Lets the user
// adjust shares / avg cost / purchase date / notes after the fact (e.g.
// to reconcile broker statements).

const EditHoldingDialog = ({ open, onOpenChange, holding, onSaved }) => {
  const [shares, setShares] = useState("");
  const [price, setPrice] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && holding) {
      setShares(holding.shares != null ? String(holding.shares) : "");
      setPrice(holding.purchase_price != null ? String(holding.purchase_price) : "");
      setPurchaseDate(holding.purchase_date ? String(holding.purchase_date).slice(0, 10) : "");
      setNotes(holding.notes || "");
    }
  }, [open, holding]);

  const submit = async () => {
    if (!holding?.holding_id) return;
    setSaving(true);
    try {
      await apiPut(`/us-holdings/${encodeURIComponent(holding.holding_id)}`, {
        shares: shares ? parseFloat(shares) : null,
        purchase_price: price ? parseFloat(price) : null,
        purchase_date: purchaseDate || null,
        notes: notes || null,
      });
      toast.success(`${holding.us_symbol} updated`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!holding) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="us-edit-holding-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" /> Edit holding · {holding.us_symbol}
          </DialogTitle>
          <DialogDescription>
            {holding.company_name} — {holding.sector || "—"}. Update the cost basis to
            reconcile with your broker. All amounts in USD.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="us-edit-shares">Shares</Label>
            <Input
              id="us-edit-shares" type="number" min="0" step="any"
              value={shares} onChange={(e) => setShares(e.target.value)}
              data-testid="us-edit-holding-shares"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="us-edit-price">Avg cost (USD)</Label>
            <Input
              id="us-edit-price" type="number" min="0" step="any"
              value={price} onChange={(e) => setPrice(e.target.value)}
              data-testid="us-edit-holding-price"
            />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label htmlFor="us-edit-date">Purchase date</Label>
            <Input
              id="us-edit-date" type="date"
              value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)}
              data-testid="us-edit-holding-date"
            />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label htmlFor="us-edit-notes">Notes (optional)</Label>
            <Input
              id="us-edit-notes"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. transferred from old broker"
              data-testid="us-edit-holding-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="us-edit-holding-submit">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ---- Sell Holding Dialog --------------------------------------------------
//
// Records a partial or full sale. Backend accumulates realized P/L on the
// holding doc and decrements the open share count.

const SellHoldingDialog = ({ open, onOpenChange, holding, onSold }) => {
  const [shares, setShares] = useState("");
  const [price, setPrice] = useState("");
  const [sellDate, setSellDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && holding) {
      setShares(holding.shares != null ? String(holding.shares) : "");
      setPrice(holding.current_price != null ? String(holding.current_price) : "");
      setSellDate(new Date().toISOString().slice(0, 10));
    }
  }, [open, holding]);

  const sharesNum = parseFloat(shares || "0");
  const priceNum = parseFloat(price || "0");
  const proceeds = sharesNum > 0 && priceNum > 0 ? sharesNum * priceNum : 0;
  const costBasis = sharesNum > 0 && holding?.purchase_price ? sharesNum * holding.purchase_price : 0;
  const realized = proceeds - costBasis;

  const submit = async () => {
    if (!holding?.holding_id) return;
    if (!sharesNum || !priceNum) {
      toast.error("Shares and sell price are required");
      return;
    }
    if (holding.shares != null && sharesNum > holding.shares + 1e-9) {
      toast.error(`You only hold ${holding.shares} shares of ${holding.us_symbol}`);
      return;
    }
    setSaving(true);
    try {
      await apiPost(`/us-holdings/${encodeURIComponent(holding.holding_id)}/sell`, {
        shares_sold: sharesNum,
        sell_price: priceNum,
        sell_date: sellDate || null,
      });
      toast.success(`${holding.us_symbol} · sold ${sharesNum} shares`, {
        description: `Realized: ${realized >= 0 ? "+" : ""}${fmtUsd(realized)}`,
      });
      onOpenChange(false);
      onSold?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sell failed");
    } finally {
      setSaving(false);
    }
  };

  if (!holding) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="us-sell-holding-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CircleDollarSign className="h-4 w-4" /> Sell · {holding.us_symbol}
          </DialogTitle>
          <DialogDescription>
            Record a sale to track realized P/L. You can sell part or all of your
            position. All amounts in USD.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="us-sell-shares">Shares to sell</Label>
            <Input
              id="us-sell-shares" type="number" min="0" step="any"
              max={holding.shares}
              value={shares} onChange={(e) => setShares(e.target.value)}
              data-testid="us-sell-holding-shares"
            />
            <p className="text-[11px] text-muted-foreground">Currently holding {holding.shares}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="us-sell-price">Sell price (USD)</Label>
            <Input
              id="us-sell-price" type="number" min="0" step="any"
              value={price} onChange={(e) => setPrice(e.target.value)}
              data-testid="us-sell-holding-price"
            />
            <p className="text-[11px] text-muted-foreground">Last close: {fmtUsd(holding.current_price)}</p>
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label htmlFor="us-sell-date">Sell date</Label>
            <Input
              id="us-sell-date" type="date"
              value={sellDate} onChange={(e) => setSellDate(e.target.value)}
              data-testid="us-sell-holding-date"
            />
          </div>
          {/* Inline realized-P/L preview */}
          <div className="col-span-2 rounded-md border bg-muted/40 p-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Proceeds</span><span className="tabular-nums font-medium">{fmtUsd(proceeds)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Cost basis</span><span className="tabular-nums">{fmtUsd(costBasis)}</span></div>
            <div className="flex justify-between border-t pt-1">
              <span className="font-medium">Realized P/L</span>
              <span className={`tabular-nums font-semibold ${realized >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                {realized >= 0 ? "+" : ""}{fmtUsd(realized)}
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !sharesNum || !priceNum}
            data-testid="us-sell-holding-submit"
          >
            {saving ? "Recording…" : "Record sale"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ---- US Holdings Table (CSE-mirror layout) --------------------------------
//
// Mirrors the columns and visual treatment of `HoldingsTable` from
// `PortfolioDashboard.jsx`:
//   Company | Shares | Avg Cost | Current | Value | P/L % | Yield |
//   Sector | Valuation | Signal | Score | actions
//
// Differences vs CSE (intentional):
//   * USD currency throughout (no LKR / brokerage % column).
//   * Sector cell renders the GICS sector text (no SectorVerdictPill —
//     that's a CSE-specific feature; Phase 4 may add a US equivalent).
//   * Score cell shows the live composite score (CSE uses DataConfidenceDot
//     which doesn't apply to US fundamentals).

const UsHoldingsTable = ({ holdings, onEdit, onSell, onDelete, onAddMore }) => {
  return (
    <div className="rounded-lg border overflow-hidden" data-testid="us-holdings-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Company</th>
              <th className="px-3 py-2 text-right font-medium">Shares</th>
              <th className="px-3 py-2 text-right font-medium">Avg Cost</th>
              <th className="px-3 py-2 text-right font-medium">Current</th>
              <th className="px-3 py-2 text-right font-medium">Value</th>
              <th className="px-3 py-2 text-right font-medium">P/L %</th>
              <th className="px-3 py-2 text-right font-medium hidden md:table-cell">Yield</th>
              <th className="px-3 py-2 text-left font-medium hidden lg:table-cell">Sector</th>
              <th className="px-3 py-2 text-center font-medium hidden md:table-cell">Valuation</th>
              <th className="px-3 py-2 text-center font-medium hidden md:table-cell">Signal</th>
              <th className="px-3 py-2 text-right font-medium hidden md:table-cell">Score</th>
              <th className="px-3 py-2 text-right font-medium w-[80px]"></th>
            </tr>
          </thead>
          <tbody>
            {holdings.map((h) => {
              const up = (h.gain_loss_pct ?? 0) >= 0;
              const changeClass = up ? "text-emerald-600 dark:text-emerald-400" : "text-destructive";
              const Arrow = up ? ArrowUpRight : ArrowDownRight;
              const yieldPct = h.dividend_yield_pct;
              return (
                <tr
                  key={h.holding_id}
                  className="border-t border-border hover:bg-muted/30 transition-colors"
                  data-testid={`us-holding-row-${h.us_symbol}`}
                >
                  <td className="px-3 py-2 align-middle">
                    <Link
                      to={`/us-stocks/${encodeURIComponent(h.us_symbol)}`}
                      className="font-medium text-sm text-primary hover:underline"
                      data-testid={`us-holding-row-name-${h.us_symbol}`}
                    >
                      {h.company_name}
                    </Link>
                    <div className="text-[11px] text-muted-foreground font-mono">
                      {h.us_symbol}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{h.shares}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(h.purchase_price)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtUsd(h.current_price)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtUsd(h.current_value_usd)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-medium ${changeClass}`}>
                    <span className="inline-flex items-center gap-0.5 whitespace-nowrap justify-end">
                      <Arrow className="h-3 w-3" />
                      {fmtPct(h.gain_loss_pct, 1)}
                    </span>
                    <div className={`text-[10px] ${changeClass}`}>
                      {h.gain_loss_usd != null
                        ? `${h.gain_loss_usd >= 0 ? "+" : ""}${fmtUsd(h.gain_loss_usd, { compact: true })}`
                        : ""}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums hidden md:table-cell">
                    {yieldPct != null ? `${yieldPct.toFixed(2)}%` : "—"}
                  </td>
                  <td className="px-3 py-2 text-left hidden lg:table-cell text-xs">
                    {h.sector || <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-center hidden md:table-cell">
                    <ValBadge s={h.valuation_status} />
                  </td>
                  <td className="px-3 py-2 text-center hidden md:table-cell">
                    <SignalBadge s={h.signal} />
                  </td>
                  <td className="px-3 py-2 text-right hidden md:table-cell">
                    {h.composite_score != null ? (
                      <span className="tabular-nums font-mono text-xs">
                        {Math.round(h.composite_score)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label="Actions"
                          data-testid={`us-holding-row-actions-${h.us_symbol}`}
                        >
                          <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuLabel>{h.us_symbol}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onEdit(h)}
                          data-testid={`us-holding-row-edit-${h.us_symbol}`}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onAddMore(h)}
                          data-testid={`us-holding-row-add-more-${h.us_symbol}`}
                        >
                          <PlusIcon className="h-3.5 w-3.5 mr-2" /> Add more shares
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onSell(h)}
                          data-testid={`us-holding-row-sell-${h.us_symbol}`}
                        >
                          <CircleDollarSign className="h-3.5 w-3.5 mr-2" /> Sell shares
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onDelete(h)}
                          className="text-destructive focus:text-destructive"
                          data-testid={`us-holding-row-delete-${h.us_symbol}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};



// ---- Main panel -----------------------------------------------------------

const UsPortfolioPanel = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem("us_active_portfolio") || null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  // Edit / sell / delete state — Phase 2 actions on the holdings table.
  // Must be declared before any early returns to satisfy the rules of hooks.
  const [editHolding, setEditHolding] = useState(null);
  const [sellHolding, setSellHolding] = useState(null);
  const [addMoreSeed, setAddMoreSeed] = useState(null);
  // Active sub-tab — controlled so we can auto-jump to Health after a
  // brand-new portfolio is created (Fix 3 onboarding).
  const [activeTab, setActiveTab] = useState("holdings");

  useEffect(() => { if (activeId) localStorage.setItem("us_active_portfolio", activeId); }, [activeId]);

  const loadList = useCallback(async () => {
    const list = await apiGet("/us-portfolios");
    setPortfolios(list || []);
    return list || [];
  }, []);

  const loadSummary = useCallback(async (id) => {
    if (!id) { setSummary(null); return; }
    const s = await apiGet(`/us-portfolios/${id}/summary`);
    setSummary(s);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const list = await loadList();
        if (cancelled) return;
        if (list.length === 0) {
          setActiveId(null); setSummary(null);
        } else {
          let chosen = activeId;
          if (!list.some((p) => p.portfolio_id === chosen)) chosen = list[0].portfolio_id;
          setActiveId(chosen);
          await loadSummary(chosen);
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeId) return;
    loadSummary(activeId).catch(() => {});
  }, [activeId, loadSummary]);

  const onCreated = async (created) => {
    await loadList();
    setActiveId(created.portfolio_id);
    // Onboarding (Fix 3): a brand-new portfolio has zero holdings, so the
    // Holdings tab would be a barren empty state. Jump straight to Health
    // — that tab now renders an empty-portfolio CTA card with "Get AI
    // Suggestions" + "Add stocks manually" calls-to-action.
    setActiveTab("health");
    toast.success("Portfolio created", {
      description: `${created.name || ""} · ${(created.investment_style || "balanced").toUpperCase()} style · ${(created.risk_profile || "moderate")} risk`,
    });
  };

  const onHoldingAdded = async () => {
    if (activeId) await loadSummary(activeId);
  };

  const removeHolding = async (holdingId) => {
    if (!window.confirm("Remove this holding?")) return;
    try {
      await apiDelete(`/us-holdings/${holdingId}`);
      toast.success("Holding removed");
      if (activeId) await loadSummary(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (portfolios.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center" data-testid="us-portfolio-empty">
          <Globe2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <div className="text-base font-semibold mb-1">No US portfolios yet</div>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
            Create a USD-only portfolio for your S&P 500 holdings. CSE portfolios remain entirely separate (no currency conversion).
          </p>
          <Button onClick={() => setCreateOpen(true)} data-testid="us-portfolio-create-empty">
            <Plus className="h-4 w-4 mr-1.5" /> Create US portfolio
          </Button>
        </CardContent>
        <CreateUsPortfolioDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={onCreated} />
      </Card>
    );
  }

  // Edit / sell / delete state — Phase 2 actions on the holdings table.
  const onEditHolding = (h) => setEditHolding(h);
  const onSellHolding = (h) => setSellHolding(h);
  const onAddMoreShares = (h) => {
    setAddMoreSeed({ symbol: h.us_symbol, last_price: h.current_price });
    setAddOpen(true);
  };
  const onDeleteHolding = async (h) => {
    if (!h?.holding_id) return;
    if (typeof window !== "undefined") {
      const ok = window.confirm(
        `Delete ${h.us_symbol} (${h.shares} shares, ${fmtUsd(h.current_value_usd)})?\n\n` +
        `This removes the holding entirely. Use "Sell shares" instead if you want to keep realized P/L history.`,
      );
      if (!ok) return;
    }
    try {
      await apiDelete(`/us-holdings/${encodeURIComponent(h.holding_id)}`);
      toast.success(`${h.us_symbol} removed`);
      await loadSummary(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const totals = summary?.totals;
  const holdings = summary?.holdings || [];
  const activePortfolio = portfolios.find((p) => p.portfolio_id === activeId) || null;
  const styleLabel = (activePortfolio?.investment_style || "").toUpperCase();

  return (
    <div className="space-y-5" data-testid="us-portfolio-panel">
      {/* Selector + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={activeId || ""} onValueChange={setActiveId}>
            <SelectTrigger className="w-64" data-testid="us-portfolio-selector"><SelectValue /></SelectTrigger>
            <SelectContent>
              {portfolios.map((p) => (
                <SelectItem key={p.portfolio_id} value={p.portfolio_id}>{p.portfolio_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {styleLabel ? (
            <Badge variant="outline" className="font-medium" data-testid="us-portfolio-style-badge">
              {styleLabel}
            </Badge>
          ) : null}
          <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)} data-testid="us-portfolio-create-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New portfolio
          </Button>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="us-portfolio-add-holding-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add holding
        </Button>
      </div>

      {/* Three sub-tabs to mirror the CSE Portfolio Dashboard layout.
          Phase 1 ships only Holdings live; Projection + Health get
          phase-aware placeholders so users see the roadmap. */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4" data-testid="us-portfolio-tabs">
        <TabsList className="grid w-full max-w-xl grid-cols-3" data-testid="us-portfolio-tabs-list">
          <TabsTrigger value="holdings" data-testid="us-portfolio-tab-holdings">
            <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Holdings
          </TabsTrigger>
          <TabsTrigger value="projection" data-testid="us-portfolio-tab-projection">
            <Calculator className="h-3.5 w-3.5 mr-1.5" /> Projection
          </TabsTrigger>
          <TabsTrigger value="health" data-testid="us-portfolio-tab-health">
            <Heart className="h-3.5 w-3.5 mr-1.5" /> Health
          </TabsTrigger>
        </TabsList>

        {/* ============ HOLDINGS TAB ============ */}
        <TabsContent value="holdings" className="mt-2 space-y-5">
          {/* Totals card */}
          {totals ? (
            <Card>
              <CardContent className="pt-5 pb-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="us-portfolio-totals">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Invested (USD)</div>
                    <div className="text-xl font-semibold tabular-nums">{fmtUsd(totals.invested_amount_usd)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Current (USD)</div>
                    <div className="text-xl font-semibold tabular-nums">{fmtUsd(totals.current_value_usd)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Gain/Loss</div>
                    <div className={`text-xl font-semibold tabular-nums ${totals.gain_loss_usd >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                      {totals.gain_loss_usd >= 0 ? "+" : ""}{fmtUsd(totals.gain_loss_usd)}
                    </div>
                    <div className={`text-xs tabular-nums ${totals.gain_loss_pct >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                      {totals.gain_loss_pct >= 0 ? "+" : ""}{fmtPct(totals.gain_loss_pct, 2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Holdings</div>
                    <div className="text-xl font-semibold tabular-nums">{totals.holdings_count}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* Holdings table (CSE-mirror layout) */}
          <Card>
            <CardHeader className="pb-3 flex-row items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="h-4 w-4" /> Holdings
              </CardTitle>
              <span className="text-xs text-muted-foreground">All amounts in USD</span>
            </CardHeader>
            <CardContent className="p-0">
              {holdings.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground" data-testid="us-portfolio-empty-holdings">
                  No holdings yet. <button className="underline text-primary" onClick={() => setAddOpen(true)}>Add your first US holding</button>.
                </div>
              ) : (
                <UsHoldingsTable
                  holdings={holdings}
                  onEdit={onEditHolding}
                  onSell={onSellHolding}
                  onDelete={onDeleteHolding}
                  onAddMore={onAddMoreShares}
                />
              )}
            </CardContent>
          </Card>

          {/* Vs benchmark chart */}
          {activeId && holdings.length > 0 ? <VsBenchmarkChart portfolioId={activeId} /> : null}
        </TabsContent>

        {/* ============ PROJECTION TAB (Phase 3 — live) ============ */}
        <TabsContent value="projection" className="mt-2">
          {activeId ? (
            <UsReturnProjectionPanel
              portfolioId={activeId}
              testIdPrefix="us-projection"
            />
          ) : (
            <Card data-testid="us-portfolio-projection-no-portfolio">
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                Select a portfolio to see projections.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ============ HEALTH TAB (Phase 4 — live) ============ */}
        <TabsContent value="health" className="mt-2">
          {activeId ? (
            <UsPortfolioHealthTab
              portfolioId={activeId}
              onChanged={() => loadSummary(activeId)}
              onRequestAddHolding={() => setAddOpen(true)}
            />
          ) : (
            <Card data-testid="us-portfolio-health-no-portfolio">
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                Select a portfolio to see health.
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <CreateUsPortfolioDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={onCreated} />
      <AddHoldingDialog
        open={addOpen}
        onOpenChange={(o) => { if (!o) setAddMoreSeed(null); setAddOpen(o); }}
        portfolioId={activeId}
        onAdded={onHoldingAdded}
        seedSymbol={addMoreSeed?.symbol}
        seedPrice={addMoreSeed?.last_price}
      />
      <EditHoldingDialog
        open={!!editHolding}
        onOpenChange={(o) => { if (!o) setEditHolding(null); }}
        holding={editHolding}
        onSaved={() => { setEditHolding(null); if (activeId) loadSummary(activeId); }}
      />
      <SellHoldingDialog
        open={!!sellHolding}
        onOpenChange={(o) => { if (!o) setSellHolding(null); }}
        holding={sellHolding}
        onSold={() => { setSellHolding(null); if (activeId) loadSummary(activeId); }}
      />
    </div>
  );
};

export default UsPortfolioPanel;
